import { Component, Fragment, type ReactNode } from "react"
import { Link, useParams } from "@tanstack/react-router"
import { ChevronRight, PanelLeft, RotateCw } from "lucide-react"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { Tip } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"
import { useShellState } from "./shell-state"

/** Brings the sidebar back when it is collapsed, and opens it on small screens. */
function SidebarToggle() {
  const { t } = useTranslation("shell")
  const { sidebarCollapsed, toggleSidebar } = useShellState()
  return (
    <Tip label={t("sidebar.toggle")} keys="mod+b">
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label={t("sidebar.toggle")}
        onClick={toggleSidebar}
        className={cn("-ml-1", !sidebarCollapsed && "md:hidden")}
      >
        <PanelLeft aria-hidden strokeWidth={1.75} />
      </Button>
    </Tip>
  )
}

/**
 * An ancestor of the page, linked to its own page. `to` is filled in from the
 * current URL's workspace and project.
 */
export type Crumb = {
  label: string
  to:
    | "/$workspaceSlug"
    | "/$workspaceSlug/$projectSlug"
    | "/$workspaceSlug/$projectSlug/tickets"
    | "/$workspaceSlug/$projectSlug/issues"
    | "/$workspaceSlug/$projectSlug/documents"
}

/**
 * The 44px bar at the top of every page: where you are on the left, what you
 * can do here on the right. `crumbs` are the ancestors of `title`.
 */
export function PageHeader({
  title,
  crumbs = [],
  actions,
}: {
  title: string
  crumbs?: Crumb[]
  actions?: ReactNode
}) {
  const { workspaceSlug = "", projectSlug = "" } = useParams({ strict: false })
  return (
    <header className="flex h-(--header-height) shrink-0 items-center gap-2 border-b px-3 md:px-4">
      <SidebarToggle />
      <div className="flex min-w-0 items-center gap-1.5">
        {crumbs.map((crumb) => (
          <Fragment key={crumb.to}>
            <Link
              to={crumb.to}
              params={{ workspaceSlug, projectSlug }}
              className="truncate rounded-sm text-muted-foreground transition-colors hover:text-foreground max-sm:hidden"
            >
              {crumb.label}
            </Link>
            <ChevronRight
              aria-hidden
              strokeWidth={1.75}
              className="size-3.5 shrink-0 text-tertiary max-sm:hidden"
            />
          </Fragment>
        ))}
        <h1 className="type-section truncate">{title}</h1>
      </div>
      {actions ? <div className="ml-auto flex shrink-0 items-center gap-1">{actions}</div> : null}
    </header>
  )
}

/** The scrolling body under a `PageHeader`. */
export function PageBody({
  width = "full",
  className,
  children,
}: {
  /** `narrow` for forms read top to bottom, `full` for lists and boards. */
  width?: "narrow" | "full"
  className?: string
  children: ReactNode
}) {
  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <div
        className={cn(
          width === "narrow" && "mx-auto w-full max-w-2xl px-4 py-6 md:px-6 md:py-8",
          className,
        )}
      >
        {children}
      </div>
    </div>
  )
}

/** One line and at most one action. */
export function EmptyState({
  title,
  action,
  className,
}: {
  title: string
  action?: ReactNode
  className?: string
}) {
  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3 text-muted-foreground md:px-6",
        className,
      )}
    >
      <p>{title}</p>
      {action}
    </div>
  )
}

export function ErrorState({
  onRetry,
  className,
}: {
  onRetry?: () => void
  className?: string
}) {
  const { t } = useTranslation()
  return (
    <div
      role="alert"
      className={cn(
        "flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3 text-muted-foreground md:px-6",
        className,
      )}
    >
      <p>{t("states.error")}</p>
      {onRetry ? (
        <Button variant="outline" size="sm" onClick={onRetry}>
          <RotateCw aria-hidden strokeWidth={1.75} className="size-3.5" />
          {t("actions.retry")}
        </Button>
      ) : null}
    </div>
  )
}

const SKELETON_WIDTHS = ["w-2/5", "w-3/5", "w-1/3", "w-1/2", "w-2/3", "w-1/4"]

/** Rows shaped like the list they stand in for. */
export function ListSkeleton({ rows = 6, className }: { rows?: number; className?: string }) {
  const { t } = useTranslation()
  return (
    <div role="status" aria-label={t("states.loading")} className={className}>
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="flex h-9 items-center gap-3 border-b px-4 md:px-6">
          <Skeleton className="size-4 rounded-full" />
          <Skeleton className={cn("h-3", SKELETON_WIDTHS[index % SKELETON_WIDTHS.length])} />
        </div>
      ))}
    </div>
  )
}

type BoundaryProps = {
  children: ReactNode
  fallback?: (retry: () => void) => ReactNode
  /** When this changes, a failed boundary clears itself: pass the pathname. */
  resetKey?: string
}

/**
 * Convex surfaces a failed query by throwing during render. This catches it
 * and offers a retry, which remounts the subtree and so re-subscribes.
 */
export class QueryBoundary extends Component<BoundaryProps, { failed: boolean; attempt: number }> {
  state = { failed: false, attempt: 0 }

  static getDerivedStateFromError() {
    return { failed: true }
  }

  componentDidCatch(error: unknown) {
    // The fallback tells the user it failed; this tells the developer why.
    console.error(error)
  }

  componentDidUpdate(previous: BoundaryProps) {
    // Leaving the page that failed is as good as a retry.
    if (this.state.failed && previous.resetKey !== this.props.resetKey) this.retry()
  }

  retry = () => this.setState(({ attempt }) => ({ failed: false, attempt: attempt + 1 }))

  render() {
    if (this.state.failed) {
      return this.props.fallback ? this.props.fallback(this.retry) : <ErrorState onRetry={this.retry} />
    }
    return <Fragment key={this.state.attempt}>{this.props.children}</Fragment>
  }
}
